import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePaginatedApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { clientsAPI, transporteursAPI } from '../services/api';
import { formatDate, getClientSizeDisplay, formatCurrency } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import MultiSelect from '../components/MultiSelect';
import Modal, { ConfirmModal } from '../components/Modal';
import ClientCreditModal from '../components/ClientCreditModal';
import PeseurQuickClientModal from '../components/PeseurQuickClientModal';
import PeseurPonctuelClientModal from '../components/PeseurPonctuelClientModal';
import { BuildingOfficeIcon, PlusIcon, EyeIcon, PencilIcon, TrashIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, KeyIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import FcfaIcon from '../components/FcfaIcon';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const isSystemGeneratedPonctuelEmail = (email) =>
  typeof email === 'string' && email.toLowerCase().includes('@ponctuel.iportcloud.invalid');

const ClientsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const openClientFromNavHandledRef = useRef(null);
  const toast = useToast();
  const { isManagerOrAdmin, user, isAccountant, isSuper } = useAuth();
  const superReadOnly = isSuper();
  const isClientUser = user?.role === 'CLIENT';
  const isPeseurUser = user?.role === 'PESEUR';
  const isAccountantUser = isAccountant();
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showPeseurQuickClientModal, setShowPeseurQuickClientModal] = useState(false);
  const [showPonctuelClientModal, setShowPonctuelClientModal] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateLoginModalOpen, setIsCreateLoginModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editFormData, setEditFormData] = useState(null); // Full client with transporteurClients when editing
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  /** PAYING = prepaid (left tab); CREDIT = encours — drives list filter for all roles except CLIENT (server ignores filter for CLIENT). */
  const [billingTab, setBillingTab] = useState('PAYING');

  const {
    data: clients,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh
  } = usePaginatedApi(clientsAPI.getAll, { billingType: 'PAYING' });

  // Open client modal when navigating from another page (e.g. bill details)
  useEffect(() => {
    const openClientId = location.state?.openClientId;
    if (!openClientId) {
      openClientFromNavHandledRef.current = null;
      return;
    }
    if (openClientFromNavHandledRef.current === openClientId) return;
    openClientFromNavHandledRef.current = openClientId;

    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        const response = await clientsAPI.getById(openClientId);
        const clientFromApi = response?.data?.client;
        if (!clientFromApi) return;
        setSelectedClient(clientFromApi);
        setIsViewModalOpen(true);
        if (!isClientUser) {
          const bt =
            clientFromApi.billingType === 'PAYING'
              ? 'PAYING'
              : clientFromApi.billingType === 'PONCTUEL'
                ? 'PONCTUEL'
                : 'CREDIT';
          setBillingTab(bt);
          updateParams({ billingType: bt, page: 1 });
        }
      } catch (error) {
        console.error('Error opening client from navigation:', error);
        toast.error(error?.response?.data?.message || t('common.error'));
      }
    })();
  }, [location.state?.openClientId, location.pathname, navigate, t, toast, isClientUser, updateParams]);

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    clientsAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refresh();
      },
      successMessage: 'Client created successfully',
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => clientsAPI.update(selectedClient.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedClient(null);
        refresh();
      },
      successMessage: 'Client updated successfully',
    }
  );

  const { execute, loading: deleteLoading } = useAsyncOperation();

  // Ref to store the debounce timeout ID
  const searchTimeoutRef = useRef(null);

  const handleSearch = () => {
    updateParams({ search });
  };

  // Auto-search with debounce when search value changes
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      updateParams({ search });
    }, 300); // 300ms debounce delay

    // Cleanup function to clear timeout
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]); // Only trigger when search value changes

  // Event handlers
  const handleView = (client) => {
    setSelectedClient(client);
    setIsViewModalOpen(true);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditFormData(null);
    setIsEditModalOpen(true);
  };

  // Fetch full client (with transporteurClients) when edit modal opens
  useEffect(() => {
    if (!isEditModalOpen || !selectedClient?.id) {
      if (!isEditModalOpen) setEditFormData(null);
      return;
    }
    let cancelled = false;
    clientsAPI.getById(selectedClient.id)
      .then((res) => {
        if (!cancelled && res?.data?.client) setEditFormData(res.data.client);
      })
      .catch(() => {
        if (!cancelled) setEditFormData(null);
      });
    return () => { cancelled = true; };
  }, [isEditModalOpen, selectedClient?.id]);

  const handleDelete = (client) => {
    setSelectedClient(client);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedClient) return;
    
    try {
      await execute(
        () => clientsAPI.delete(selectedClient.id),
        {
          showSuccessToast: true,
          successMessage: 'Client deleted successfully',
        }
      );
      setIsDeleteModalOpen(false);
      setSelectedClient(null);
      refresh();
    } catch (error) {
      // Error is already handled by useAsyncOperation with toast
      // Don't need to do anything here
    }
  };

  // Export clients to Excel
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await clientsAPI.export();
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('clients.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || t('clients.exportError'));
    } finally {
      setExportLoading(false);
    }
  };

  // Import clients from Excel
  const handleImport = async (file) => {
    try {
      setImportLoading(true);
      const response = await clientsAPI.import(file);
      
      const { results } = response.data;
      const message = response.data.message;
      
      // Show detailed results
      if (results.errors.length > 0 || results.skipped.length > 0) {
        toast.error(
          `${message}\n${results.errors.length > 0 ? `Errors: ${results.errors.length}` : ''}\n${results.skipped.length > 0 ? `Skipped: ${results.skipped.length}` : ''}`,
          { duration: 5000 }
        );
      } else {
        toast.success(message);
      }
      
      setIsImportModalOpen(false);
      refresh();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || t('clients.importError'));
    } finally {
      setImportLoading(false);
    }
  };

  const columns = useMemo(() => {
    const base = [
    {
      key: 'companyName',
      title: t('clients.companyName'),
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.contactName}</div>
        </div>
      ),
    },
    {
      key: 'contactPhone',
      title: t('clients.contactPhone'),
      render: (_value, row) => (
        <div>
          <div className="text-sm text-gray-900">{row.contactPhone || '—'}</div>
          {row.contactEmail && !isSystemGeneratedPonctuelEmail(row.contactEmail) && (
            <div className="text-sm text-gray-500">{row.contactEmail}</div>
          )}
        </div>
      ),
    },
    ];

    if (billingTab !== 'PONCTUEL') {
      base.push({
        key: 'size',
        title: t('clients.size'),
        render: (value) => (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            {getClientSizeDisplay(value)}
          </span>
        ),
      });
    }

    base.push(
    {
      key: 'billingType',
      title: t('clients.billingType'),
      render: (value) => (
        <span className="text-xs text-gray-700">
          {value === 'PAYING'
            ? t('clients.billingTypePaying')
            : value === 'PONCTUEL'
              ? t('clients.billingTypePonctuel')
              : t('clients.billingTypeCredit')}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('clients.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'active' ? 'bg-green-100 text-green-800' : value === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
        }`}>
          {t(`clients.${value}`)}
        </span>
      ),
    },
    );

    if (billingTab !== 'PONCTUEL') {
      base.push({
        key: 'creditBalance',
        title: t('clients.creditBalance'),
        render: (value) => (
          <div className="flex items-center gap-1">
            <FcfaIcon className="h-4 w-4 text-green-600" />
            <span className={`font-medium ${(value || 0) > 0 ? 'text-green-600' : 'text-gray-600'}`}>
              {formatCurrency(value || 0)}
            </span>
          </div>
        ),
      });
    }

    base.push(
    {
      key: 'createdAt',
      title: t('tables.date'),
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '240px',
      minWidth: 240,
      render: (value, row) => (
        <div className="flex items-center flex-wrap gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => handleView(row)} 
            title={t('common.view')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {(!isPeseurUser || row.billingType === 'PAYING' || row.billingType === 'PONCTUEL') &&
            !isAccountantUser && !superReadOnly && (
          <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title="Edit">
            <PencilIcon className="h-4 w-4" />
          </Button>
          )}
          {isManagerOrAdmin() && !superReadOnly && !row.userId && !row.user && !row.hasLoginWithEmail && (
            <Button size="sm" variant="ghost" onClick={() => { setSelectedClient(row); setIsCreateLoginModalOpen(true); }} title={t('clients.createLogin')}>
              <KeyIcon className="h-4 w-4" />
            </Button>
          )}
          {((isManagerOrAdmin() && !superReadOnly) || (isPeseurUser && row.billingType === 'PONCTUEL')) && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleDelete(row)} 
              title={
                row.canDelete === false
                  ? (row._count?.loads > 0 ? t('clients.tooltips.cannotDeleteWithLoads') : t('clients.tooltips.cannotDeleteWithTransporteurs'))
                  : t('common.delete')
              }
              className={row.canDelete !== true ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-700"}
              disabled={row.canDelete !== true}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
    );

    return base;
  }, [billingTab, t, isPeseurUser, isAccountantUser, isManagerOrAdmin, superReadOnly]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start space-x-2 sm:space-x-3 min-w-0 flex-1">
          <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('clients.title')}</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">
              {isPeseurUser ? t('clients.peseurClientsThreeTabsSubtitle') : t('clients.title')}
            </p>
            {isPeseurUser && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" onClick={() => setShowPeseurQuickClientModal(true)}>
                  <UserPlusIcon className="h-4 w-4 sm:mr-2" />
                  {t('weighing.addPrepaidClient')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowPonctuelClientModal(true)}>
                  <UserPlusIcon className="h-4 w-4 sm:mr-2" />
                  {t('clients.addPonctuelClient')}
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {isManagerOrAdmin() && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleExport} disabled={exportLoading} className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <ArrowDownTrayIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{exportLoading ? t('common.loading') : t('clients.export')}</span>
              <span className="sm:hidden">{t('clients.export')}</span>
            </Button>
            {!superReadOnly && (
              <>
                <Button variant="outline" onClick={() => setIsImportModalOpen(true)} disabled={importLoading} className="flex-1 sm:flex-initial text-xs sm:text-sm">
                  <ArrowUpTrayIcon className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('clients.import')}</span>
                  <span className="sm:hidden">{t('clients.import')}</span>
                </Button>
                <Button onClick={() => setIsAddModalOpen(true)} className="flex-1 sm:flex-initial text-xs sm:text-sm">
                  <PlusIcon className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('clients.addClient')}</span>
                  <span className="sm:hidden">{t('clients.addClient')}</span>
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="card">
        {!isClientUser && (
          <div
            className="mb-4 flex flex-wrap gap-2 border-b border-gray-200 pb-3"
            role="tablist"
            aria-label={t('clients.billingTabsAria')}
          >
            <button
              type="button"
              role="tab"
              aria-selected={billingTab === 'PAYING'}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                billingTab === 'PAYING'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => {
                if (billingTab === 'PAYING') return;
                setBillingTab('PAYING');
                updateParams({ billingType: 'PAYING', page: 1 });
              }}
            >
              {t('clients.tabPrepaidClients')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingTab === 'CREDIT'}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                billingTab === 'CREDIT'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => {
                if (billingTab === 'CREDIT') return;
                setBillingTab('CREDIT');
                updateParams({ billingType: 'CREDIT', page: 1 });
              }}
            >
              {t('clients.tabCreditClients')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingTab === 'PONCTUEL'}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                billingTab === 'PONCTUEL'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => {
                if (billingTab === 'PONCTUEL') return;
                setBillingTab('PONCTUEL');
                updateParams({ billingType: 'PONCTUEL', page: 1 });
              }}
            >
              {t('clients.tabPonctuelClients')}
            </button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
          />
          <Button onClick={handleSearch}>{t('common.search')}</Button>
        </div>

        <Table
          columns={columns}
          data={clients}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('messages.noDataFound')}
        />
      </div>

      {/* Add Modal */}
      <ClientForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        title={t('clients.newClient')}
        submitText={t('common.create')}
        canEditBilling={isManagerOrAdmin()}
        canCreateTransporteur={isManagerOrAdmin()}
      />

      {/* Edit Modal */}
      <ClientForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedClient(null);
          setEditFormData(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        title={t('clients.editClient')}
        submitText={t('common.update')}
        initialData={editFormData || selectedClient}
        isClientUser={user?.role === 'CLIENT'}
        canEditBilling={isManagerOrAdmin()}
        clientDetailLoading={Boolean(selectedClient?.id && !editFormData)}
        canCreateTransporteur={isManagerOrAdmin() || isPeseurUser}
      />

      {/* View Client Modal */}
      <ViewClientModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        allowEditFromView={
          !superReadOnly &&
          !isAccountantUser &&
          (!isPeseurUser ||
            selectedClient?.billingType === 'PAYING' ||
            selectedClient?.billingType === 'PONCTUEL')
        }
        allowManageCreditsTab={selectedClient?.billingType !== 'PONCTUEL' && !superReadOnly}
        onCreditsUpdatedList={refresh}
        onEdit={(clientToEdit) => {
          setIsViewModalOpen(false);
          setSelectedClient(clientToEdit);
          setEditFormData(null);
          setIsEditModalOpen(true);
        }}
      />

      {isPeseurUser && (
        <>
          <PeseurQuickClientModal
            isOpen={showPeseurQuickClientModal}
            onClose={() => setShowPeseurQuickClientModal(false)}
            onClientCreated={() => {
              setBillingTab('PAYING');
              updateParams({ billingType: 'PAYING', page: 1 });
              refresh();
            }}
          />
          <PeseurPonctuelClientModal
            isOpen={showPonctuelClientModal}
            onClose={() => setShowPonctuelClientModal(false)}
            onClientCreated={() => {
              setBillingTab('PONCTUEL');
              updateParams({ billingType: 'PONCTUEL', page: 1 });
              refresh();
            }}
          />
        </>
      )}

      {/* Create login for client */}
      <CreateLoginClientModal
        isOpen={isCreateLoginModalOpen}
        onClose={() => {
          setIsCreateLoginModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onSuccess={() => {
          refresh();
          setIsCreateLoginModalOpen(false);
          setSelectedClient(null);
          toast.success(t('clients.loginCreated'));
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedClient(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('clients.deleteClientTitle')}
        message={t('clients.deleteClientMessage', { name: selectedClient?.companyName || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
      />

      {/* Import Modal */}
      <ImportClientsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImport}
        loading={importLoading}
      />
    </div>
  );
};

// Client Form Component
const ClientForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  title,
  submitText,
  initialData = {},
  isClientUser = false,
  canEditBilling = false,
  /** Edit modal: true until GET /clients/:id returns (avoids empty transporteurIds + wrong generate button). */
  clientDetailLoading = false,
  /** Creating a transporteur requires ADMIN/MANAGER on the API. */
  canCreateTransporteur = true,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    size: 'MEDIUM',
    status: 'active',
    transporteurIds: [],
    billingType: 'CREDIT',
    outstandingBillLimit: '',
    creditWeighingHold: false
  });
  const [transporteurs, setTransporteurs] = useState([]);
  const [transporteursLoading, setTransporteursLoading] = useState(false);
  const [generateTransporteurLoading, setGenerateTransporteurLoading] = useState(false);
  const [showTransporteurRequiredModal, setShowTransporteurRequiredModal] = useState(false);
  const [liveBillingStatus, setLiveBillingStatus] = useState(null);
  const [liveBillingLoading, setLiveBillingLoading] = useState(false);
  const [outstandingLimitFocused, setOutstandingLimitFocused] = useState(false);
  const toast = useToast();

  const formatOutstandingLimitFr = (val) => {
    if (val === '' || val == null) return '';
    const cleaned = String(val).replace(/\s/g, '').replace(/\u202f/g, '');
    const n = parseFloat(cleaned.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return '';
    return new Intl.NumberFormat('fr-FR', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(n);
  };

  // Fetch transporteurs for dropdown
  React.useEffect(() => {
    if (!isOpen) return;
    setTransporteursLoading(true);
    transporteursAPI.getAll({ limit: 500 })
      .then((res) => setTransporteurs(res?.data?.transporteurs || []))
      .catch(() => setTransporteurs([]))
      .finally(() => setTransporteursLoading(false));
  }, [isOpen]);

  // Use primitive deps to avoid infinite loop when initialData is default {} (new ref every render)
  const initialDataId = initialData?.id;
  const initialDataTcLength = initialData?.transporteurClients?.length ?? -1;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initialData object ref would cause infinite setState loop; we re-run only when id or loaded transporteurClients length changes
  React.useEffect(() => {
    if (isOpen) {
      const linkedIds = (initialData?.transporteurClients || [])
        .map(tc => tc.transporteur?.id)
        .filter(Boolean);
      setFormData({
        companyName: initialData?.companyName || '',
        address: initialData?.address || '',
        contactName: initialData?.contactName || '',
        contactEmail:
          initialData?.contactEmail && !isSystemGeneratedPonctuelEmail(initialData.contactEmail)
            ? initialData.contactEmail
            : '',
        contactPhone: initialData?.contactPhone || '',
        size: initialData?.size || 'MEDIUM',
        status: initialData?.status || 'active',
        transporteurIds: linkedIds.length ? linkedIds : [],
        billingType: initialData?.billingType || 'CREDIT',
        outstandingBillLimit:
          initialData?.outstandingBillLimit != null && initialData?.outstandingBillLimit !== ''
            ? String(initialData.outstandingBillLimit)
            : '',
        creditWeighingHold: Boolean(initialData?.creditWeighingHold)
      });
    } else {
      setFormData({
        companyName: '',
        address: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        size: 'MEDIUM',
        status: 'active',
        transporteurIds: [],
        billingType: 'CREDIT',
        outstandingBillLimit: '',
        creditWeighingHold: false
      });
    }
  }, [isOpen, initialDataId, initialDataTcLength]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize client form when opening or switching the edited client

  React.useEffect(() => {
    if (!isOpen || !canEditBilling || isClientUser || !initialDataId) {
      setLiveBillingStatus(null);
      return undefined;
    }
    let cancelled = false;
    setLiveBillingLoading(true);
    clientsAPI
      .getBillingStatus(initialDataId)
      .then((data) => {
        if (!cancelled) setLiveBillingStatus(data);
      })
      .catch(() => {
        if (!cancelled) setLiveBillingStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLiveBillingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, canEditBilling, isClientUser, initialDataId]);

  React.useEffect(() => {
    if (!isOpen) setOutstandingLimitFocused(false);
  }, [isOpen]);

  const billingTypeEffective = formData.billingType || 'CREDIT';
  const isPonctuelBillingForm = billingTypeEffective === 'PONCTUEL';
  const isContactEmailRequired = isClientUser || !isPonctuelBillingForm;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (clientDetailLoading) return;

    if (
      !formData.companyName ||
      !formData.contactName ||
      (!isPonctuelBillingForm && !formData.contactEmail?.trim()) ||
      !formData.address
    ) {
      alert(t('clients.fillRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailTrim = String(formData.contactEmail || '').trim();
    if (!isPonctuelBillingForm) {
      if (!emailTrim || !emailRegex.test(emailTrim)) {
        alert(t('clients.validEmail'));
        return;
      }
    } else if (emailTrim && !emailRegex.test(emailTrim)) {
      alert(t('clients.validEmail'));
      return;
    }

    const isEditMode = Boolean(initialData?.id);
    const transporteurLinksLoaded =
      !isEditMode || Array.isArray(initialData.transporteurClients);

    // When status is active, at least one transporteur is required (not for CLIENT profile; skip until edit payload is loaded)
    if (
      !isClientUser &&
      formData.status === 'active' &&
      !formData.transporteurIds?.length &&
      transporteurLinksLoaded
    ) {
      setShowTransporteurRequiredModal(true);
      return;
    }

    const payload = { ...formData };
    if (payload.billingType === 'PONCTUEL') {
      payload.size = payload.size || 'MEDIUM';
      const em = String(payload.contactEmail || '').trim();
      const isShadowPonctuelEmail = em.toLowerCase().includes('@ponctuel.iportcloud.invalid');
      payload.contactEmail = em && !isShadowPonctuelEmail ? em : null;
    }
    if (canEditBilling && !isClientUser) {
      payload.billingType = formData.billingType || 'CREDIT';
      if (payload.billingType === 'PAYING' || payload.billingType === 'PONCTUEL') {
        payload.outstandingBillLimit = null;
      } else {
        const lim = String(formData.outstandingBillLimit || '')
          .replace(/\s/g, '')
          .replace(/\u202f/g, '')
          .trim();
        payload.outstandingBillLimit = lim === '' ? null : parseFloat(lim.replace(',', '.'));
      }
      payload.creditWeighingHold = Boolean(formData.creditWeighingHold);
    } else {
      delete payload.billingType;
      delete payload.outstandingBillLimit;
      delete payload.creditWeighingHold;
    }
    onSubmit(payload);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    if (name === 'billingType' && (value === 'PAYING' || value === 'PONCTUEL')) {
      setFormData((prev) => ({
        ...prev,
        billingType: value,
        outstandingBillLimit: '',
        ...(value === 'PONCTUEL' ? { contactEmail: '' } : {})
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateTransporteur = async () => {
    if (!formData.companyName?.trim() || !formData.contactName?.trim() || !formData.contactEmail?.trim() || !formData.contactPhone?.trim() || !formData.address?.trim()) {
      toast.error(t('clients.fillRequired'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contactEmail)) {
      toast.error(t('clients.validEmail'));
      return;
    }
    setGenerateTransporteurLoading(true);
    try {
      const payload = {
        companyName: formData.companyName.trim(),
        address: formData.address.trim(),
        contactName: formData.contactName.trim(),
        contactEmail: formData.contactEmail.trim(),
        contactPhone: formData.contactPhone.trim(),
        status: 'active'
      };
      if (initialData?.id) payload.clientIds = [initialData.id];
      const res = await transporteursAPI.create(payload);
      const tr = res?.data?.transporteur;
      if (tr?.id) {
        setTransporteurs(prev => [...prev, { id: tr.id, companyName: tr.companyName || formData.companyName }]);
        setFormData(prev => ({ ...prev, transporteurIds: [...(prev.transporteurIds || []), tr.id] }));
        toast.success(t('clients.transporteurGenerated'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('clients.transporteurGenerateError'));
    } finally {
      setGenerateTransporteurLoading(false);
    }
  };

  const clientCompanyNorm = (formData.companyName || '').trim().toLowerCase();
  const hasSameClientTransporteur = clientCompanyNorm
    ? transporteurs.some(tr => (tr.companyName || '').trim().toLowerCase() === clientCompanyNorm)
    : false;
  const isEditMode = Boolean(initialData?.id);
  const transporteurLinksLoaded =
    !isEditMode || Array.isArray(initialData.transporteurClients);
  const noLinkedTransporteurs = !(formData.transporteurIds?.length);
  // New client: pending + no link + no same-name carrier. Edit: loaded details, no links, no same-name carrier (any status).
  const showGenerateTransporteurButton =
    canCreateTransporteur &&
    transporteurLinksLoaded &&
    !hasSameClientTransporteur &&
    noLinkedTransporteurs &&
    (formData.status === 'pending' || isEditMode) &&
    (formData.billingType || 'CREDIT') !== 'PONCTUEL';

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={loading}
            form="client-form"
            disabled={clientDetailLoading}
            title={clientDetailLoading ? t('common.loading') : undefined}
          >
            {submitText}
          </Button>
        </>
      }
    >
      <form id="client-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Company Name */}
          <div className="md:col-span-2">
            <Input
              label={t('clients.companyName')}
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Enter company name"
              required
            />
          </div>

          {/* Contact Name */}
          <div>
            <Input
              label={t('clients.contactName')}
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              placeholder="Enter contact person name"
              required
            />
          </div>

          {/* Contact email (shown for all client types; optional for ponctuel billing) */}
          <div>
            <Input
              label={t('clients.contactEmail')}
              name="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={handleChange}
              placeholder="Enter contact email"
              required={isContactEmailRequired}
            />
          </div>

          {/* Contact Phone */}
          <div>
            <Input
              label={t('clients.contactPhone')}
              name="contactPhone"
              type="tel"
              value={formData.contactPhone}
              onChange={handleChange}
              placeholder="Enter contact phone"
              required
            />
          </div>

          {/* Company size (not for walk-in / ponctuel clients; hidden for CLIENT own profile) */}
          {!isClientUser && formData.billingType !== 'PONCTUEL' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.size')} *
            </label>
            <select
              name="size"
              value={formData.size}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="SMALL">{t('clients.small')}</option>
              <option value="MEDIUM">{t('clients.medium')}</option>
              <option value="LARGE">{t('clients.large')}</option>
            </select>
          </div>
          )}

          {/* Status (hidden for CLIENT user editing own profile) */}
          {!isClientUser && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.status')} *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="active">{t('clients.active')}</option>
              <option value="inactive">{t('clients.inactive')}</option>
              <option value="pending">{t('clients.pending')}</option>
            </select>
          </div>
          )}

          {/* Billing (managers/admins only) */}
          {canEditBilling && !isClientUser && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.billingType')}
                </label>
                <select
                  name="billingType"
                  value={formData.billingType || 'CREDIT'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="CREDIT">{t('clients.billingTypeCredit')}</option>
                  <option value="PAYING">{t('clients.billingTypePaying')}</option>
                  <option value="PONCTUEL">{t('clients.billingTypePonctuel')}</option>
                </select>
              </div>
              {(formData.billingType || 'CREDIT') === 'CREDIT' && (
              <div>
                <Input
                  label={t('clients.outstandingBillLimit')}
                  name="outstandingBillLimit"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={
                    outstandingLimitFocused
                      ? formData.outstandingBillLimit
                      : formatOutstandingLimitFr(formData.outstandingBillLimit)
                  }
                  onFocus={() => setOutstandingLimitFocused(true)}
                  onBlur={() => setOutstandingLimitFocused(false)}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    setFormData((prev) => ({ ...prev, outstandingBillLimit: digits }));
                  }}
                  placeholder="—"
                  inputClassName="tabular-nums"
                />
                <p className="text-xs text-gray-500 mt-1">{t('clients.outstandingBillLimitHint')}</p>
              </div>
              )}
              <div className="md:col-span-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                <span className="font-medium text-gray-700">{t('clients.currentOutstandingBills')}: </span>
                {liveBillingLoading ? (
                  <span className="text-gray-500">{t('common.loading')}</span>
                ) : liveBillingStatus != null ? (
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {formatCurrency(liveBillingStatus.outstanding)}
                  </span>
                ) : (
                  <span className="text-gray-500">—</span>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('clients.currentOutstandingBillsHint')}</p>
              </div>
              {(formData.billingType || 'CREDIT') === 'CREDIT' && (
                <div className="md:col-span-2 checkbox-field flex items-start gap-2">
                  <input
                    id="creditWeighingHold"
                    name="creditWeighingHold"
                    type="checkbox"
                    checked={Boolean(formData.creditWeighingHold)}
                    onChange={handleChange}
                    className="checkbox-app mt-1"
                  />
                  <label htmlFor="creditWeighingHold" className="text-sm text-gray-700">
                    <span className="font-medium">{t('clients.creditWeighingHold')}</span>
                    <span className="block text-gray-500">{t('clients.creditWeighingHoldHint')}</span>
                  </label>
                </div>
              )}
            </>
          )}

          {/* Transporteurs (hidden for CLIENT user editing own profile) */}
          {!isClientUser && (
          <div className="md:col-span-2">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <MultiSelect
                  label={t('clients.transporteurs')}
                  name="transporteurIds"
                  values={formData.transporteurIds || []}
                  onChange={handleChange}
                  options={transporteurs.map(tr => ({ value: tr.id, label: tr.companyName }))}
                  placeholder={t('clients.selectTransporteurs')}
                  disabled={transporteursLoading}
                  required={formData.status !== 'pending'}
                />
              </div>
              {showGenerateTransporteurButton && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerateTransporteur}
                  loading={generateTransporteurLoading}
                  disabled={generateTransporteurLoading || transporteursLoading}
                  className="shrink-0"
                >
                  {t('clients.generateTransporteur')}
                </Button>
              )}
            </div>
            {showGenerateTransporteurButton && (
              <p className="text-xs text-gray-500 mt-1">
                {isEditMode
                  ? t('clients.generateTransporteurHintEdit')
                  : t('clients.generateTransporteurHint')}
              </p>
            )}
          </div>
          )}

          {/* Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.address')} *
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter company address"
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>
      </form>
    </Modal>

    <Modal
      isOpen={showTransporteurRequiredModal}
      onClose={() => setShowTransporteurRequiredModal(false)}
      title={t('clients.statusValidationTitle') || 'Validation'}
      size="sm"
      footer={
        <Button variant="primary" onClick={() => setShowTransporteurRequiredModal(false)}>
          {t('common.ok') || 'OK'}
        </Button>
      }
    >
      <p className="text-sm text-gray-700">
        {t('clients.atLeastOneTransporteur') || 'Lorsque le statut est actif, au moins un transporteur doit être lié à ce client.'}
      </p>
    </Modal>
    </>
  );
};

// Import Clients Modal Component
const ImportClientsModal = ({ isOpen, onClose, onImport, loading }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        alert(t('clients.invalidFileType'));
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      onImport(file);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('clients.importClients')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('clients.selectExcelFile')}
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              cursor-pointer"
          />
          <p className="mt-2 text-sm text-gray-500">
            {t('clients.importInstructions')}
          </p>
        </div>

        {file && (
          <div className="p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{t('clients.selectedFile')}:</strong> {file.name}
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!file || loading}>
            {loading ? t('common.loading') : t('clients.import')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Create login for client - clients can now log in
const CreateLoginClientModal = ({ isOpen, onClose, client, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  /** Avoid browser saved credentials filling the modal until the user focuses a field. */
  const [fieldsUnlocked, setFieldsUnlocked] = useState(false);

  // Check if client has a pending password from self-registration
  const hasPendingPassword = client?.hasPendingPassword;

  // Preload username with client email when modal opens (app data, not browser vault)
  React.useEffect(() => {
    if (!isOpen) {
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setFieldsUnlocked(false);
      return;
    }
    setFieldsUnlocked(false);
    if (client?.contactEmail) {
      setUsername(client.contactEmail);
    } else {
      setUsername('');
    }
  }, [isOpen, client?.id, client?.contactEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client?.id || !username.trim()) return;
    
    // Password validation only if not using pending password
    if (!hasPendingPassword) {
      if (!password) {
        toast.error(t('clients.passwordRequired'));
        return;
      }
      if (password !== confirmPassword) {
        toast.error(t('clients.passwordMismatch'));
        return;
      }
      if (password.length < 6) {
        toast.error(t('clients.passwordMinLength'));
        return;
      }
    } else if (password && password !== confirmPassword) {
      // If overriding pending password, validate it
      toast.error(t('clients.passwordMismatch'));
      return;
    } else if (password && password.length < 6) {
      toast.error(t('clients.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      const payload = { username: username.trim() };
      // Only include password if provided (backend will use pending password if not)
      if (password) {
        payload.password = password;
      }
      await clientsAPI.createLogin(client.id, payload);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Form is valid if username is provided, and either:
  // 1. Client has pending password (password fields optional), or
  // 2. Password fields are filled and match
  const formValid = username.trim() && (
    hasPendingPassword 
      ? (!password || (password === confirmPassword && password.length >= 6))
      : (password && confirmPassword && password === confirmPassword && password.length >= 6)
  );

  if (!client) return null;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('clients.createLogin')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!formValid}>{t('common.create')}</Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">{t('clients.createLoginFor', { name: client.companyName })}</p>
      {hasPendingPassword && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            {t('clients.hasPendingPassword')}
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        <Input
          label={t('clients.loginEmail')}
          type="email"
          name="client_new_login_identifier"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onFocus={() => setFieldsUnlocked(true)}
          readOnly={!fieldsUnlocked}
          autoComplete="off"
          placeholder={t('clients.loginEmailPlaceholder')}
          required
        />
        <Input
          label={hasPendingPassword ? t('clients.passwordOptional') : t('common.password')}
          type="password"
          name="client_new_login_secret"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFieldsUnlocked(true)}
          readOnly={!fieldsUnlocked}
          autoComplete="new-password"
          required={!hasPendingPassword}
          minLength={6}
          placeholder={hasPendingPassword ? t('clients.passwordOptionalPlaceholder') : t('clients.passwordPlaceholder')}
        />
        {(password || !hasPendingPassword) && (
          <Input
            label={t('clients.confirmPassword')}
            type="password"
            name="client_new_login_secret_confirm"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onFocus={() => setFieldsUnlocked(true)}
            readOnly={!fieldsUnlocked}
            autoComplete="new-password"
            required={!hasPendingPassword || !!password}
            minLength={6}
            placeholder={t('clients.confirmPasswordPlaceholder')}
          />
        )}
      </form>
    </Modal>
  );
};

// View Client Modal Component
const ViewClientModal = ({
  isOpen,
  onClose,
  client,
  onEdit,
  allowEditFromView = true,
  allowManageCreditsTab = true,
  onCreditsUpdatedList
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const loadBilling = useCallback(async () => {
    if (!client?.id) return;
    setBillingLoading(true);
    try {
      const data = await clientsAPI.getBillingStatus(client.id);
      setBilling(data);
    } catch (_e) {
      setBilling(null);
      toast.error(t('clients.outstandingLoadError'));
    } finally {
      setBillingLoading(false);
    }
  }, [client?.id, t, toast]);

  useEffect(() => {
    if (!isOpen || !client?.id) {
      setBilling(null);
      return;
    }
    loadBilling();
  }, [isOpen, client?.id, loadBilling]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('overview');
      return;
    }
    if (!allowManageCreditsTab) {
      setActiveTab('overview');
    }
  }, [isOpen, client?.id, allowManageCreditsTab]);

  if (!client) return null;

  const isPonctuelClientView = client.billingType === 'PONCTUEL';

  const outstandingBills = billing?.outstandingBills || [];
  const listedOutstandingRemainingTotal = outstandingBills.reduce(
    (sum, row) => sum + Math.max(0, Number(row.remainingAmount || 0)),
    0
  );
  const limitVal = billing?.outstandingBillLimit;
  const hasLimit = billing?.hasOutstandingBillLimit;
  const isPayingOrPonctuelClient =
    billing?.billingType === 'PAYING' || billing?.billingType === 'PONCTUEL';
  const showOutstandingCeiling = !isPayingOrPonctuelClient;

  let percentOfOutstandingLimit = null;
  if (
    billing &&
    showOutstandingCeiling &&
    hasLimit &&
    limitVal != null &&
    Number(limitVal) > 0
  ) {
    percentOfOutstandingLimit = Math.min(
      999,
      Math.round((Number(billing.outstanding ?? 0) / Number(limitVal)) * 1000) / 10
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('clients.viewClient')} size="lg">
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {client.companyName}
            </h3>
            <p className="text-sm text-gray-500">{client.contactName}</p>
          </div>
        </div>

        {allowManageCreditsTab ? (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('clients.viewClient')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('credits')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'credits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('clients.manageCredits')}
            </button>
          </nav>
        </div>
        ) : null}

        {activeTab === 'overview' && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.companyName')}</label>
            <p className="mt-1 text-sm text-gray-900">{client.companyName || '-'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.contactName')}</label>
            <p className="mt-1 text-sm text-gray-900">{client.contactName || '-'}</p>
          </div>
          
          {!isPonctuelClientView && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.contactEmail')}</label>
            <p className="mt-1 text-sm text-gray-900">{client.contactEmail || '-'}</p>
          </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.contactPhone')}</label>
            <p className="mt-1 text-sm text-gray-900">{client.contactPhone || '-'}</p>
          </div>
          
          {!isPonctuelClientView && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.size')}</label>
            <span className="mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
              {getClientSizeDisplay(client.size)}
            </span>
          </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('clients.status')}</label>
            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              client.status === 'active' ? 'bg-green-100 text-green-800' : client.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
            }`}>
              {t(`clients.${client.status}`)}
            </span>
          </div>
          
          {!isPonctuelClientView && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t('clients.address')}</label>
            <p className="mt-1 text-sm text-gray-900">{client.address || '-'}</p>
          </div>
          )}
          
          {!isPonctuelClientView && (
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.creditBalance')}</label>
              <div className="mt-1 flex items-center gap-1">
                <FcfaIcon className="h-4 w-4 text-green-600" />
                <span className={`font-medium ${(client.creditBalance || 0) > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                  {formatCurrency(client.creditBalance || 0)}
                </span>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('tables.date')}</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(client.createdAt)}</p>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/80">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('clients.outstandingSectionTitle')}</h4>
          {billingLoading ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : billing ? (
            <>
              <div
                className={`grid gap-3 mb-4 ${
                  showOutstandingCeiling ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
                }`}
              >
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('clients.outstandingRemainingTotalLabel')}</p>
                  <div className="mt-1 flex items-center gap-1">
                    <FcfaIcon className="h-4 w-4 text-amber-600" />
                    <span className={`text-sm font-semibold ${(billing.outstandingRemainingSum || 0) > 0 ? 'text-amber-800' : 'text-gray-700'}`}>
                      {formatCurrency(billing.outstandingRemainingSum || 0)}
                    </span>
                  </div>
                </div>
                {showOutstandingCeiling && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('clients.outstandingCeilingLabel')}</p>
                    <div className="mt-1 flex items-center gap-1">
                      <FcfaIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-semibold text-gray-900">
                        {hasLimit && limitVal != null ? formatCurrency(limitVal) : t('clients.outstandingNoCeiling')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {percentOfOutstandingLimit != null && (
                <div className="mb-4 space-y-2 rounded-md border border-gray-200 bg-white px-3 py-3">
                  <div className="flex flex-wrap justify-between items-center gap-2 text-xs text-gray-600">
                    <span>{t('clients.pendingInvoicesPercentOfLimit')}</span>
                    <span
                      className={`font-bold tabular-nums ${
                        percentOfOutstandingLimit >= 100
                          ? 'text-red-700'
                          : percentOfOutstandingLimit >= 80
                            ? 'text-amber-700'
                            : 'text-gray-800'
                      }`}
                    >
                      {percentOfOutstandingLimit}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        percentOfOutstandingLimit >= 100
                          ? 'bg-red-500'
                          : percentOfOutstandingLimit >= 80
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, percentOfOutstandingLimit)}%` }}
                    />
                  </div>
                </div>
              )}
              {showOutstandingCeiling && hasLimit && billing.overLimit && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5 mb-3">{t('clients.billing.creditOverLimit')}</p>
              )}
              {showOutstandingCeiling && hasLimit && billing.warnCreditNearLimit && !billing.overLimit && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">{t('clients.billing.creditNearLimit')}</p>
              )}
              {outstandingBills.length === 0 ? (
                <p className="text-sm text-gray-600">{t('clients.outstandingNoBills')}</p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">
                          <th className="px-3 py-2">{t('bills.billNumber')}</th>
                          <th className="px-3 py-2">{t('bills.dueDate')}</th>
                          <th className="px-3 py-2 text-right">{t('bills.totalAmount')}</th>
                          <th className="px-3 py-2 text-right">{t('clients.outstandingRemaining')}</th>
                          <th className="px-3 py-2">{t('tables.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {outstandingBills.map((row) => (
                          <tr key={row.id}>
                            <td className="px-3 py-2">
                              {isAdmin() ? (
                                <span className="font-mono text-xs text-gray-800">{row.billNumber}</span>
                              ) : (
                                <button
                                  type="button"
                                  className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  onClick={() => {
                                    onClose?.();
                                    navigate('/bills', {
                                      state: { openBillId: row.id, filterClientId: client.id }
                                    });
                                  }}
                                >
                                  {row.billNumber}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.dueDate)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.totalAmount)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-amber-900">{formatCurrency(row.remainingAmount)}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                row.status === 'OVERDUE' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {row.status === 'OVERDUE' ? t('bills.overdue') : t('bills.pending')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-amber-50/90 border-t-2 border-amber-200/80">
                          <td
                            colSpan={3}
                            className="px-3 py-2.5 text-right text-sm font-medium text-gray-800 align-middle"
                          >
                            {t('clients.pendingInvoicesTotalListed')}
                          </td>
                          <td className="px-3 py-2.5 text-right align-middle">
                            <span className="text-base font-bold text-amber-900 tabular-nums inline-flex items-center justify-end gap-1">
                              <FcfaIcon className="h-4 w-4 shrink-0" />
                              {formatCurrency(listedOutstandingRemainingTotal)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 bg-amber-50/90" aria-hidden="true" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
              <p className="mt-3 text-xs text-gray-500">
                {billing?.billingType === 'PONCTUEL'
                  ? t('clients.outstandingLimitFootnotePonctuel')
                  : isPayingOrPonctuelClient
                    ? t('clients.outstandingLimitFootnotePaying')
                    : t('clients.outstandingLimitFootnote')}
              </p>
              {!isAdmin() && (
              <div className="mt-2">
                <Link
                  to="/bills"
                  state={{ filterClientId: client.id }}
                  onClick={() => onClose?.()}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {t('clients.outstandingOpenBills')}
                </Link>
              </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">{t('clients.outstandingLoadError')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          {allowEditFromView ? (
            <Button
              variant="outline"
              onClick={() => onEdit?.(client)}
            >
              {t('clients.editClient')}
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
        </>
        )}

        {allowManageCreditsTab && activeTab === 'credits' && (
          <ClientCreditModal
            isOpen={isOpen && activeTab === 'credits'}
            onClose={onClose}
            client={client}
            onCreditUpdated={() => {
              void loadBilling();
              onCreditsUpdatedList?.();
            }}
            embedded
          />
        )}
      </div>
    </Modal>
  );
};

export default ClientsPage;